import { useCallback, useEffect, useRef } from "react";
import { anchorId } from "@karasu-tools/core";
import type { Dispatch } from "react";
import type { ShareTarget } from "@karasu-tools/core";
import type { AppAction, ActiveView } from "../state/app-reducer.js";
import { useLatestRef } from "./useLatestRef.js";

// ─── Utilities (exported for testing) ────────────────────────────────────────

/**
 * Optional hash modifiers. An object (not positional args) so callers can't
 * silently pass a value into the wrong slot — the failure mode where a
 * highlight string landed where a mode boolean was expected (see the churn when
 * the entity sub-mode was added, #1907).
 */
interface BuildHashOptions {
  /** Org Tree View mode (only meaningful when `activeView === "org"`). */
  orgTree?: boolean;
  /** Entity sub-mode (only meaningful when `activeView === "system"` and drilled). */
  entityView?: boolean;
  /** Element id to focus-highlight on arrival. */
  highlight?: string | null;
  /** Open file to carry as `?file=` (Issue #811). */
  file?: string | null;
}

/**
 * Encodes activeView + viewPath + open file into a URL hash string.
 *
 * Examples:
 *   ("deploy", [])                                          → "#krs-deploy"
 *   ("deploy", [], { highlight: "ECommerce" })              → "#krs-deploy:ECommerce"
 *   ("system", [])                                          → "#krs-system-root"
 *   ("system", ["Payment"])                                → "#krs-system-Payment"
 *   ("org", ["a", "b"])                                     → "#krs-org-b"  (last segment only)
 *   ("org", [], { orgTree: true })                         → "#krs-org-tree"  (Tree View mode)
 *   ("org", [], { highlight: "ecTeam" })                   → "#krs-org-root:ecTeam"
 *   ("system", ["Ordering"], { entityView: true })         → "#krs-entity-Ordering"  (entity sub-mode)
 *   ("system", [], { file: "/p/before.krs" })              → "#krs-system-root?file=%2Fp%2Fbefore.krs"
 */
export function buildHash(
  activeView: ActiveView,
  viewPath: string[],
  options: BuildHashOptions = {},
): string {
  const {
    orgTree: isOrgTreeView = false,
    entityView: isEntityView = false,
    highlight: highlightNodeId = null,
    file: filePath = null,
  } = options;
  // Exhaustive dispatch on activeView — adding a new ActiveView member without
  // updating this switch fails the `never` assignment at compile time. Locks in
  // TPL-1094 (the #1094 failure mode where a new tab silently fell into
  // the fallback branch and produced the wrong hash).
  let base: string;
  switch (activeView) {
    case "deploy":
      base = "#krs-deploy";
      break;
    case "matrix":
      base = "#krs-matrix";
      break;
    // system/org are drillable: their element anchors route through the shared
    // `anchorId` grammar (core), so the SPA hash and the static drill-down SVG
    // emit one form — the cross-surface parity guarded by TPL-1827.
    // (deploy/matrix above are single-level whole-view tabs with a bare
    // `#krs-<view>` token, and org Tree View is a mode — these are not element
    // anchors and intentionally do not share the leaf grammar; see
    // docs/spec/permalink.md.)
    case "org":
      base = isOrgTreeView
        ? "#krs-org-tree"
        : `#${anchorId("org", viewPath.length === 0 ? "root" : viewPath[viewPath.length - 1])}`;
      break;
    case "system":
      // Entity sub-mode: while drilled into a domain, the same drill path is
      // addressed with the `entity` prefix (mirrors the static
      // #krs-entity-<domainId> bundle level). The sub-mode has no meaning at the
      // system root (no domain drilled), so it falls back to the system anchor.
      base =
        isEntityView && viewPath.length > 0
          ? `#${anchorId("entity", viewPath[viewPath.length - 1])}`
          : `#${anchorId("system", viewPath.length === 0 ? "root" : viewPath[viewPath.length - 1])}`;
      break;
    default: {
      const _exhaustive: never = activeView;
      throw new Error(`unhandled activeView: ${String(_exhaustive)}`);
    }
  }
  const withHighlight = highlightNodeId ? `${base}:${highlightNodeId}` : base;
  return filePath ? `${withHighlight}?file=${encodeURIComponent(filePath)}` : withHighlight;
}

/**
 * Convert a deep permalink {@link ShareTarget} (carried inside a shared
 * `#s=` payload, #1827) into the canonical `#krs-<view>-<node>:highlight`
 * hash. Reuses {@link buildHash} so the share path and the in-app navigation
 * path emit one grammar. The leaf `node` becomes the single-segment viewPath;
 * `useHistoryNavigation` reconstructs the full path from it via the node-path
 * index on mount, exactly as a normal drill hash resolves.
 */
export function shareTargetToHash(target: ShareTarget): string {
  return buildHash(target.view, target.node ? [target.node] : [], {
    orgTree: target.orgTree ?? false,
    entityView: target.entityView ?? false,
    highlight: target.highlight ?? null,
  });
}

/**
 * Decodes a URL hash string into { activeView, nodeId, isOrgTreeView, highlightNodeId, filePath }.
 * nodeId is null for root and tree-view hashes.
 * highlightNodeId is extracted from the optional colon-suffixed segment (e.g. "#krs-deploy:ECommerce").
 * filePath is extracted from the optional `?file=<encoded>` suffix (Issue #811).
 * Returns null for unrecognized hashes.
 */
export function parseHash(hash: string): {
  activeView: ActiveView;
  nodeId: string | null;
  isOrgTreeView: boolean;
  isEntityView: boolean;
  highlightNodeId: string | null;
  filePath: string | null;
} | null {
  // Strip the `?file=` suffix first so it doesn't interfere with `:` parsing.
  let filePath: string | null = null;
  let core = hash;
  const queryIdx = hash.indexOf("?", 1);
  if (queryIdx !== -1) {
    const query = hash.slice(queryIdx + 1);
    core = hash.slice(0, queryIdx);
    const params = new URLSearchParams(query);
    const f = params.get("file");
    if (f) filePath = f;
  }

  // Extract optional highlight suffix: "#krs-deploy:ECommerce" → base="#krs-deploy", highlight="ECommerce"
  const colonIdx = core.indexOf(":", 1);
  let highlightNodeId: string | null = null;
  let base = core;
  if (colonIdx !== -1) {
    highlightNodeId = core.slice(colonIdx + 1) || null;
    base = core.slice(0, colonIdx);
  }

  // Exhaustive dispatch table — `satisfies Record<ActiveView, ...>` on
  // PARSE_DISPATCHERS makes adding a new ActiveView a compile error here as
  // well as in buildHash. Each dispatcher matches a disjoint prefix, so
  // iteration order is irrelevant.
  for (const view of Object.keys(PARSE_DISPATCHERS) as ActiveView[]) {
    const parsed = PARSE_DISPATCHERS[view](base);
    if (parsed) return { ...parsed, highlightNodeId, filePath };
  }
  return null;
}

type ParseResult = {
  activeView: ActiveView;
  nodeId: string | null;
  isOrgTreeView: boolean;
  isEntityView: boolean;
};

const PARSE_DISPATCHERS = {
  deploy: (base) =>
    base === "#krs-deploy"
      ? { activeView: "deploy", nodeId: null, isOrgTreeView: false, isEntityView: false }
      : null,
  matrix: (base) =>
    base === "#krs-matrix"
      ? { activeView: "matrix", nodeId: null, isOrgTreeView: false, isEntityView: false }
      : null,
  org: (base) => {
    if (base === "#krs-org-tree")
      return { activeView: "org", nodeId: null, isOrgTreeView: true, isEntityView: false };
    const m = base.match(/^#krs-org-(.+)$/);
    if (!m) return null;
    return {
      activeView: "org",
      nodeId: m[1] === "root" ? null : m[1],
      isOrgTreeView: false,
      isEntityView: false,
    };
  },
  system: (base) => {
    // The entity view is a system-view sub-mode keyed by the drilled domain id,
    // so its #krs-entity-<domainId> hash resolves to activeView "system" with
    // the entity sub-mode on. Checked before the plain system anchor.
    const em = base.match(/^#krs-entity-(.+)$/);
    if (em) {
      return {
        activeView: "system",
        nodeId: em[1] === "root" ? null : em[1],
        isOrgTreeView: false,
        isEntityView: true,
      };
    }
    const m = base.match(/^#krs-system-(.+)$/);
    if (!m) return null;
    return {
      activeView: "system",
      nodeId: m[1] === "root" ? null : m[1],
      isOrgTreeView: false,
      isEntityView: false,
    };
  },
} satisfies Record<ActiveView, (base: string) => ParseResult | null>;

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useHistoryNavigation({
  activeView,
  viewPath,
  currentFilePath,
  nodePathIndex,
  orgPathIndex,
  dispatch,
  isOrgTreeView,
  setIsOrgTreeView,
  isEntityView,
  setIsEntityView,
  highlightedNodeId,
  onFileChange,
}: {
  activeView: ActiveView;
  viewPath: string[];
  currentFilePath: string | null;
  /** Maps system service/domain IDs to their viewPath in the system/deploy view. */
  nodePathIndex: Map<string, string[]>;
  /** Maps org team IDs to the viewPath needed to show that team's children. Optional. */
  orgPathIndex?: Map<string, string[]>;
  dispatch: Dispatch<AppAction>;
  isOrgTreeView: boolean;
  setIsOrgTreeView: (v: boolean) => void;
  /** Entity sub-mode: system view drilled into a domain, showing its entity view. */
  isEntityView: boolean;
  setIsEntityView: (v: boolean) => void;
  highlightedNodeId: string | null;
  /**
   * Called when popstate (or initial-mount hash) requests a different file.
   * Implementations should read the file's content and dispatch SELECT_FILE.
   * Optional — when omitted, file changes are not restored from the URL
   * (used by modes without per-file navigation, e.g. ServeMode/MemoryMode).
   * Issue #811.
   */
  onFileChange?: (path: string) => Promise<void> | void;
}): {
  navigateActiveView: (view: ActiveView) => void;
  navigateViewPath: (path: string[]) => void;
} {
  // Tracks the current activeView without causing stale closures in effects
  const activeViewRef = useLatestRef(activeView);

  // Stable ref for setIsOrgTreeView to avoid re-running mount-only effects
  const setIsOrgTreeViewRef = useLatestRef(setIsOrgTreeView);

  // Stable ref for setIsEntityView (same rationale as setIsOrgTreeView).
  const setIsEntityViewRef = useLatestRef(setIsEntityView);

  // When true, state changes are caused by popstate — skip pushing another history entry
  const isProgrammaticNavRef = useRef(false);

  // nodeId extracted from the initial hash, resolved once the relevant path index is ready
  const pendingNodeIdRef = useRef<string | null>(null);
  // activeView associated with the pending nodeId (determines which index to use)
  const pendingActiveViewRef = useRef<ActiveView>("system");
  // highlight extracted from the initial hash, re-applied once the model-derived
  // index is ready (Issue #1842). On a shared open, the project-seed effect's
  // SELECT_FILE → VIEW_RESET runs *after* effect ① and wipes the highlight it
  // dispatched on mount. This deferral mirrors the node re-resolution so the
  // selected node stays focus-highlighted.
  const pendingHighlightRef = useRef<string | null>(null);

  // Stable ref for onFileChange — referenced inside long-lived effects without re-running them.
  const onFileChangeRef = useLatestRef(onFileChange);

  // Tracks the latest currentFilePath without re-binding the popstate listener.
  const currentFilePathRef = useLatestRef(currentFilePath);

  // Tracks the *previous* currentFilePath so effect ③ can distinguish
  // user-initiated file switches (push) from project-switch initialization
  // (replace) and transient null states (skip). Issue #811.
  const prevFilePathRef = useRef<string | null>(currentFilePath);

  // ① Parse initial hash on mount and set pending resolution if needed
  useEffect(() => {
    const parsed = parseHash(location.hash);
    if (!parsed) {
      history.replaceState(
        null,
        "",
        buildHash(activeView, viewPath, {
          orgTree: isOrgTreeView,
          entityView: isEntityView,
          file: currentFilePath,
        }),
      );
      return;
    }
    // Restore the open file from the hash if it differs (Issue #811).
    // The file load is async; downstream restoration (viewPath, etc.) still
    // proceeds with the parsed values — SELECT_FILE resets viewPath in the
    // reducer, but the deferred-resolution effect ② re-applies the parsed
    // nodeId once the path index is ready.
    if (parsed.filePath && parsed.filePath !== currentFilePath && onFileChangeRef.current) {
      void onFileChangeRef.current(parsed.filePath);
    }
    // Stash the highlight for deferred re-application in effect ②. The immediate
    // dispatch below sets it on mount, but the seed's VIEW_RESET wipes it; the
    // pending ref restores it once the index is ready (Issue #1842).
    if (parsed.highlightNodeId !== null) {
      pendingHighlightRef.current = parsed.highlightNodeId;
    }
    // Restore activeView from hash if different (include highlightNodeId in the transition)
    if (parsed.activeView !== activeViewRef.current) {
      dispatch({
        type: "SET_ACTIVE_VIEW",
        activeView: parsed.activeView,
        highlightNodeId: parsed.highlightNodeId,
      });
    } else if (parsed.highlightNodeId !== null) {
      dispatch({ type: "SET_HIGHLIGHTED_NODE", nodeId: parsed.highlightNodeId });
    }
    // Restore org tree view mode
    if (parsed.isOrgTreeView) {
      setIsOrgTreeViewRef.current(true);
    }
    // Restore entity sub-mode
    if (parsed.isEntityView) {
      setIsEntityViewRef.current(true);
    }
    if (parsed.nodeId === null) {
      dispatch({ type: "SET_VIEW_PATH", path: [] });
    } else {
      pendingNodeIdRef.current = parsed.nodeId;
      pendingActiveViewRef.current = parsed.activeView;
      // Resolution is deferred to effect ② once the relevant path index is populated
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ② Resolve pendingNodeId and re-apply the pending highlight once the
  // relevant path index is available.
  useEffect(() => {
    const nodeId = pendingNodeIdRef.current;
    const isOrgPending = pendingActiveViewRef.current === "org";
    const index = isOrgPending ? orgPathIndex : nodePathIndex;

    if (nodeId !== null) {
      if (!index || index.size === 0) return;
      const resolvedPath = index.get(nodeId) ?? [];
      pendingNodeIdRef.current = null;
      dispatch({ type: "SET_VIEW_PATH", path: resolvedPath });
    }

    // Re-apply the highlight wiped by the seed's VIEW_RESET (Issue #1842).
    // Gate on a populated index so this lands *after* the SELECT_FILE that
    // cleared it — the index derives from the seeded file content, so a
    // non-empty index proves the reset has already happened. This also covers
    // the highlight-only-at-root case (no pending node), which effect ① could
    // not defer on its own.
    if (pendingHighlightRef.current !== null) {
      const indexReady =
        (!!nodePathIndex && nodePathIndex.size > 0) || (!!orgPathIndex && orgPathIndex.size > 0);
      if (!indexReady) return;
      const highlight = pendingHighlightRef.current;
      pendingHighlightRef.current = null;
      dispatch({ type: "SET_HIGHLIGHTED_NODE", nodeId: highlight });
    }
  }, [nodePathIndex, orgPathIndex, dispatch]);

  // ③ Sync state changes → hash (Issue #811)
  // Includes `currentFilePath` so file switches participate in browser
  // history. The push/replace/skip decision depends on the file transition:
  //
  //   non-null → null         skip      project switch transient (reducer
  //                                     resets currentFilePath; auto-select
  //                                     of index.krs follows)
  //   null     → non-null     replace   initial file load (mount or after
  //                                     project init) — must NOT push, or
  //                                     it wipes the forward stack
  //   non-null → non-null *   push      user-initiated file switch within
  //                                     the same project
  //
  // Without this, pressing Back across a project switch and then Forward
  // would not restore the project: the auto-`selectFile(index.krs)` after
  // SET_CURRENT_PROJECT triggers a pushState that clears forward history.
  useEffect(() => {
    if (isProgrammaticNavRef.current) return;
    const prev = prevFilePathRef.current;

    // Project-switch transient — currentFilePath momentarily null. Don't
    // emit a hash entry; the next SELECT_FILE will replaceState.
    if (currentFilePath === null && prev !== null) {
      prevFilePathRef.current = null;
      return;
    }

    const newHash = buildHash(activeView, viewPath, {
      orgTree: isOrgTreeView,
      entityView: isEntityView,
      highlight: highlightedNodeId,
      file: currentFilePath,
    });
    if (location.hash !== newHash) {
      const isInitialLoad = prev === null && currentFilePath !== null;
      if (isInitialLoad) {
        history.replaceState(null, "", newHash);
      } else {
        history.pushState(null, "", newHash);
      }
    }
    prevFilePathRef.current = currentFilePath;
  }, [activeView, viewPath, isOrgTreeView, isEntityView, highlightedNodeId, currentFilePath]);

  // ④ popstate handler — browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const parsed = parseHash(location.hash);
      if (!parsed) return;

      isProgrammaticNavRef.current = true;

      // Restore the open file first (Issue #811). Downstream dispatches
      // happen synchronously below; the async file load completes later
      // and SELECT_FILE will reset viewPath in the reducer, but by that
      // point the user-intended viewPath has already been pushed.
      if (parsed.filePath !== currentFilePathRef.current && onFileChangeRef.current) {
        if (parsed.filePath) {
          void onFileChangeRef.current(parsed.filePath);
        }
      }

      if (parsed.activeView !== activeViewRef.current) {
        dispatch({
          type: "SET_ACTIVE_VIEW",
          activeView: parsed.activeView,
          highlightNodeId: parsed.highlightNodeId,
        });
      } else {
        dispatch({ type: "SET_HIGHLIGHTED_NODE", nodeId: parsed.highlightNodeId });
      }
      setIsOrgTreeViewRef.current(parsed.isOrgTreeView);
      setIsEntityViewRef.current(parsed.isEntityView);

      const path =
        parsed.nodeId === null
          ? []
          : parsed.activeView === "org"
            ? (orgPathIndex?.get(parsed.nodeId) ?? [])
            : (nodePathIndex.get(parsed.nodeId) ?? []);
      dispatch({ type: "SET_VIEW_PATH", path });

      queueMicrotask(() => {
        isProgrammaticNavRef.current = false;
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // The `*Ref` deps are stable ref objects (their `.current` is read inside
    // the listener, not captured), so listing them satisfies exhaustive-deps
    // without changing when this effect re-registers.
  }, [
    dispatch,
    nodePathIndex,
    orgPathIndex,
    activeViewRef,
    currentFilePathRef,
    onFileChangeRef,
    setIsOrgTreeViewRef,
    setIsEntityViewRef,
  ]);

  // ─── Public API ─────────────────────────────────────────────────────────────
  // Hash updates are handled by effect ③ watching state changes.

  const navigateActiveView = useCallback(
    (view: ActiveView) => {
      dispatch({ type: "SET_ACTIVE_VIEW", activeView: view });
    },
    [dispatch],
  );

  const navigateViewPath = useCallback(
    (path: string[]) => {
      dispatch({ type: "SET_VIEW_PATH", path });
    },
    [dispatch],
  );

  return { navigateActiveView, navigateViewPath };
}
